import React, { useMemo, useState } from 'react';
import type {
  ArCalendarWeek,
  ArExistingArticle,
  ArNewArticle,
  ArStat,
  ArTab,
} from '../../growth-articles/types';
import {
  AR_CALENDAR_WEEKS,
  AR_EXISTING_ARTICLES,
  AR_NEW_ARTICLES,
  AR_STATS,
} from '../../growth-articles/mockData';
import { AR_TABS, GROWTH_ARTICLES_META } from '../../growth-articles/config';
import { Icon } from '../Icon/Icon';
import { Pagination } from '../Pagination/Pagination';
import { Segmented } from '../Segmented/Segmented';
import './growth-articles.css';

const TONE: Record<ArStat['tone'], string> = {
  success: 'var(--success)',
  gold: 'var(--gold)',
  terra: 'var(--terracotta)',
  neutral: 'var(--ink-0)',
};

const eyebrowClass = 'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

function ArCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function ArStatTile({ stat }: { stat: ArStat }) {
  return (
    <ArCard className="px-[18px] py-[15px]">
      <div className={eyebrowClass}>{stat.label}</div>
      <div
        className="mt-2 font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em]"
        style={{ color: TONE[stat.tone] }}
      >
        {stat.value}
      </div>
    </ArCard>
  );
}

function ArPill({
  label,
  accent,
  tint,
}: {
  label: string;
  accent: string;
  tint: string;
}) {
  return (
    <span
      className="inline-flex items-center rounded-[var(--r-pill)] px-2.5 py-1 text-[12px] font-bold capitalize"
      style={{ background: tint, color: accent }}
    >
      {label}
    </span>
  );
}

function ArNewCard({
  article,
  open,
  onToggle,
  onViewArticle,
  onCreateArticle,
  creatingArticleId,
}: {
  article: ArNewArticle;
  open: boolean;
  onToggle: () => void;
  onViewArticle?: (id: string) => void;
  onCreateArticle?: (id: string) => void;
  creatingArticleId?: string;
}) {
  const ready = article.status === 'ready';
  const creating = creatingArticleId === article.id;
  const priorityAccent = article.priority === 'high' ? 'var(--terracotta)' : 'var(--gold)';
  const priorityTint =
    article.priority === 'high'
      ? 'color-mix(in srgb, var(--terracotta) 12%, transparent)'
      : 'var(--gold-tint)';
  const rankLabel = article.currentRank ? `#${String(article.currentRank)}` : 'Not ranking';
  const rankColor = article.currentRank
    ? article.currentRank <= 3
      ? 'var(--success)'
      : 'var(--gold)'
    : 'var(--terracotta)';

  return (
    <ArCard
      className={[
        'mb-3 overflow-hidden',
        open ? '!border-[color-mix(in_srgb,var(--success)_45%,transparent)]' : '',
      ].join(' ')}
    >
      <div className="flex items-center gap-3 px-5 py-4">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-4 border-0 bg-transparent p-0 text-left"
        >
          <span
            className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]"
            style={{
              background: ready ? 'var(--success-tint)' : 'var(--gold-tint)',
              color: ready ? 'var(--success)' : 'var(--gold)',
            }}
          >
            <Icon kind={ready ? 'check' : 'pencil'} size={18} stroke={2} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[15px] font-semibold text-[var(--ink-0)]">{article.title}</span>
              <ArPill label={article.priority} accent={priorityAccent} tint={priorityTint} />
              <ArPill
                label={ready ? 'Ready' : 'Draft'}
                accent={ready ? 'var(--success)' : 'var(--gold)'}
                tint={ready ? 'var(--success-tint)' : 'var(--gold-tint)'}
              />
            </div>
            <p className="mt-1 text-[12.5px] text-[var(--ink-3)]">
              Targets{' '}
              <span className="font-semibold text-[var(--ink-2)]">&ldquo;{article.targetKeyword}&rdquo;</span>
              {article.volume > 0 ? (
                <>
                  {' '}
                  · {article.volume.toLocaleString()} searches/mo
                </>
              ) : null}
              {article.estTraffic !== '—' ? <> · Est. {article.estTraffic} visitors</> : null} ·{' '}
              {article.wordCount.toLocaleString()} words · {article.images} images
            </p>
          </div>
        </button>
        {ready ? (
          onViewArticle ? (
            <button
              type="button"
              onClick={() => onViewArticle(article.id)}
              className="hidden shrink-0 cursor-pointer items-center rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--success)_40%,transparent)] bg-[var(--success-tint)] px-3.5 py-[7px] text-[12px] font-semibold text-[var(--success)] sm:inline-flex"
            >
              View Article →
            </button>
          ) : (
            <span className="hidden shrink-0 items-center rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--success)_40%,transparent)] bg-[var(--success-tint)] px-3.5 py-[7px] text-[12px] font-semibold text-[var(--success)] sm:inline-flex">
              View Article →
            </span>
          )
        ) : onCreateArticle ? (
          <button
            type="button"
            onClick={() => onCreateArticle(article.id)}
            disabled={creating}
            className="hidden shrink-0 cursor-pointer items-center rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--gold)_40%,transparent)] bg-[var(--gold-tint)] px-3.5 py-[7px] text-[12px] font-semibold text-[var(--gold)] disabled:cursor-not-allowed disabled:opacity-60 sm:inline-flex"
          >
            {creating ? 'Creating…' : 'Create Article →'}
          </button>
        ) : (
          <span className="hidden shrink-0 items-center rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--gold)_40%,transparent)] bg-[var(--gold-tint)] px-3.5 py-[7px] text-[12px] font-semibold text-[var(--gold)] sm:inline-flex">
            Create Article →
          </span>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[var(--ink-4)]"
          aria-label={open ? 'Collapse article' : 'Expand article'}
        >
          <Icon
            kind="down"
            size={18}
            stroke={2}
            className="transition-transform duration-200"
            style={{ transform: open ? 'rotate(180deg)' : undefined }}
          />
        </button>
      </div>

      {open ? (
        <div className="elva-fade border-t border-[var(--cream-2)]">
          <div className="border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-4">
            <div className="text-[13px] font-semibold text-[var(--gold)]">Why this article</div>
            <p className="mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">{article.why}</p>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[var(--ink-3)]">
              <span>
                Target keyword:{' '}
                <span className="font-semibold text-[var(--success)]">&ldquo;{article.targetKeyword}&rdquo;</span>
              </span>
              <span className="text-[var(--ink-4)]">·</span>
              <span>{article.volume > 0 ? `${article.volume.toLocaleString()} searches/mo` : 'Volume pending'}</span>
              <span className="text-[var(--ink-4)]">·</span>
              <span>
                Your rank: <span className="font-semibold" style={{ color: rankColor }}>{rankLabel}</span>
              </span>
            </div>
          </div>

          <div className="border-b border-[var(--cream-2)] px-5 py-4">
            <div className="mb-2.5 text-[13px] font-semibold text-[var(--ink-1)]">Article Outline</div>
            {article.outline.length ? (
              <div className="space-y-1.5">
                {article.outline.map((item, index) => (
                  <div
                    key={item}
                    className="flex items-center gap-2.5 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3 py-2"
                  >
                    <span className="w-5 font-[var(--font-mono)] text-[11px] font-semibold text-[var(--ink-4)]">
                      {index === 0 ? 'H1' : 'H2'}
                    </span>
                    <span className="text-[13px] text-[var(--ink-2)]">{item}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[13px] text-[var(--ink-3)]">
                Outline will appear when article generation completes.
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
            <span className="text-[12px] text-[var(--ink-4)]">Scheduled: {article.week}</span>
            {ready ? (
              onViewArticle ? (
                <button
                  type="button"
                  onClick={() => onViewArticle(article.id)}
                  className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]"
                >
                  View Full Article →
                </button>
              ) : (
                <span className="inline-flex items-center rounded-[var(--r-sm)] bg-[var(--success)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]">
                  View Full Article →
                </span>
              )
            ) : onCreateArticle ? (
              <button
                type="button"
                onClick={() => onCreateArticle(article.id)}
                disabled={creating}
                className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border-0 bg-[var(--gold)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {creating ? 'Creating…' : 'Create Article →'}
              </button>
            ) : (
              <span className="inline-flex items-center rounded-[var(--r-sm)] bg-[var(--gold)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]">
                Create Article →
              </span>
            )}
          </div>
        </div>
      ) : null}
    </ArCard>
  );
}

function ArExistingCard({
  article,
  open,
  onToggle,
  onViewArticle,
}: {
  article: ArExistingArticle;
  open: boolean;
  onToggle: () => void;
  onViewArticle?: (id: string) => void;
}) {
  const replace = article.status === 'replace';
  const published = article.status === 'published';
  const statusAccent = published ? 'var(--success)' : replace ? 'var(--terracotta)' : 'var(--gold)';
  const statusTint = published
    ? 'var(--success-tint)'
    : replace
      ? 'color-mix(in srgb, var(--terracotta) 12%, transparent)'
      : 'var(--gold-tint)';
  const statusLabel = published ? 'Published' : replace ? 'Replace' : 'Update';
  const hasRevision =
    !published &&
    (article.problems.length > 0 ||
      (article.preview !== '—' && article.preview.trim() !== '') ||
      article.newOutline.length > 0);
  const keywordLabel =
    article.targetKeyword && article.targetKeyword !== '—' ? article.targetKeyword : null;

  return (
    <ArCard
      className={[
        'mb-3 overflow-hidden',
        open ? '!border-[color-mix(in_srgb,var(--gold)_45%,transparent)]' : '',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center gap-4 border-0 bg-transparent px-5 py-4 text-left"
      >
        <span
          className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: statusAccent }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-[var(--ink-0)]">{article.title}</span>
            <ArPill label={statusLabel} accent={statusAccent} tint={statusTint} />
            {article.currentRank ? (
              <ArPill
                label={`Ranking #${String(article.currentRank)}`}
                accent={'var(--success)'}
                tint={'var(--success-tint)'}
              />
            ) : null}
          </div>
          <p className="mt-1 font-[var(--font-mono)] text-[12px] text-[var(--ink-3)]">
            {article.url}
            {keywordLabel ? ` · Targets “${keywordLabel}”` : ''} · {article.wordCount} words ·
            Published {article.published}
          </p>
        </div>
        <Icon
          kind="down"
          size={18}
          stroke={2}
          className="shrink-0 text-[var(--ink-4)] transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
        />
      </button>

      {open ? (
        hasRevision ? (
          <div className="elva-fade border-t border-[var(--cream-2)]">
            <div
              className="border-b border-[var(--cream-2)] px-5 py-4"
              style={{ background: 'color-mix(in srgb, var(--terracotta) 8%, transparent)' }}
            >
              <div className="text-[13px] font-semibold text-[var(--terracotta)]">
                Problems with the current article
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {article.problems.map((problem) => (
                  <span
                    key={problem}
                    className="rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--terracotta)_30%,transparent)] bg-[var(--cream-1)] px-3 py-1.5 text-[12px] text-[var(--ink-2)]"
                  >
                    {problem}
                  </span>
                ))}
              </div>
            </div>

            <div className="border-b border-[var(--cream-2)] px-5 py-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div className="inline-flex items-center gap-2 text-[14px] font-semibold text-[var(--success)]">
                  <Icon kind="check-circ" size={16} stroke={1.9} />
                  New version ready — just copy and replace
                </div>
                <span className="text-[12px] text-[var(--ink-3)]">
                  {article.newWordCount.toLocaleString()} words · {article.newImages} images
                </span>
              </div>

              <div className="mb-4">
                <div className={`${eyebrowClass} mb-1`}>New Title</div>
                <div className="font-[var(--font-display)] text-[20px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                  {article.newTitle}
                </div>
              </div>

              <div className="mb-4">
                <div className={`${eyebrowClass} mb-1.5`}>Structure</div>
                <div className="flex flex-wrap gap-1.5">
                  {article.newOutline.map((item) => (
                    <span
                      key={item}
                      className="rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-2.5 py-1 text-[12px] text-[var(--ink-3)]"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="max-h-[300px] overflow-auto rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-5">
                <div className={eyebrowClass}>Article Preview</div>
                <div className="mt-3 space-y-3">
                  {article.preview.split('\n\n').map((para) => (
                    <p key={para} className="text-[13px] leading-[1.8] text-[var(--ink-2)]">
                      {para}
                    </p>
                  ))}
                </div>
                <div className="pt-3 text-center text-[12px] font-medium text-[var(--success)]">
                  … continues for {article.newWordCount.toLocaleString()} words total
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
              <span className="text-[12px] text-[var(--ink-4)]">Replace at: {article.url}</span>
              {onViewArticle ? (
                <button
                  type="button"
                  onClick={() => onViewArticle(article.id)}
                  className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]"
                >
                  View Full Article →
                </button>
              ) : (
                <span className="inline-flex items-center rounded-[var(--r-sm)] bg-[var(--success)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]">
                  View Full Article →
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="elva-fade border-t border-[var(--cream-2)] px-5 py-5">
            {keywordLabel ? (
              <p className="mb-3 text-[13px] text-[var(--ink-2)]">
                Targets &ldquo;{keywordLabel}&rdquo;
                {article.currentRank ? ` · Ranking #${String(article.currentRank)}` : ''}
              </p>
            ) : null}
            {article.url !== '—' ? (
              <a
                href={article.url}
                target="_blank"
                rel="noreferrer"
                className="mb-4 block truncate text-[13px] text-[var(--success)] underline-offset-2 hover:underline"
              >
                {article.url}
              </a>
            ) : null}
            <div className="flex flex-wrap items-center justify-end gap-3">
              {onViewArticle ? (
                <button
                  type="button"
                  onClick={() => onViewArticle(article.id)}
                  className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]"
                >
                  View Full Article →
                </button>
              ) : (
                <span className="inline-flex items-center rounded-[var(--r-sm)] bg-[var(--success)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]">
                  View Full Article →
                </span>
              )}
            </div>
          </div>
        )
      ) : null}
    </ArCard>
  );
}

function ArCalendar({
  weeks,
  newArticles,
  existingArticles,
  onViewArticle,
}: {
  weeks: ArCalendarWeek[];
  newArticles: ArNewArticle[];
  existingArticles: ArExistingArticle[];
  onViewArticle?: (id: string) => void;
}) {
  const articleMap = useMemo(() => {
    const map = new Map<string, ArNewArticle | ArExistingArticle>();
    for (const article of newArticles) map.set(article.id, article);
    for (const article of existingArticles) map.set(article.id, article);
    for (const week of weeks) {
      for (const entry of week.articles ?? []) {
        if (!map.has(entry.id)) {
          map.set(entry.id, {
            id: entry.id,
            title: entry.title,
            targetKeyword: entry.targetKeyword ?? '—',
            volume: 0,
            currentRank: null,
            priority: 'medium',
            estTraffic: '—',
            category: entry.category ?? 'General',
            wordCount: entry.wordCount ?? 0,
            images: 0,
            status: entry.status ?? 'draft',
            why: '—',
            outline: [],
            week: week.label,
          });
        }
      }
    }
    return map;
  }, [existingArticles, newArticles, weeks]);

  return (
    <ArCard className="p-[22px]">
      <div className="mb-5 flex items-center gap-2.5">
        <Icon kind="calendar" size={18} stroke={1.9} className="text-[var(--ai-3)]" />
        <span className="font-[var(--font-display)] text-[18px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          Publishing Calendar
        </span>
      </div>
      {weeks.length ? (
        weeks.map((week, index) => {
          const items = week.articleIds
            .map((id) => articleMap.get(id))
            .filter((article): article is ArNewArticle => Boolean(article));
          const tone: ArStat['tone'] =
            items.length >= 2 ? 'success' : items.length > 0 ? 'gold' : 'neutral';
          const toneAccent = TONE[tone];
          const toneTint =
            tone === 'success'
              ? 'var(--success-tint)'
              : tone === 'gold'
                ? 'var(--gold-tint)'
                : 'var(--surface-mute)';

          return (
            <div
              key={`${week.label}-${String(index)}`}
              className="py-5"
              style={{
                borderBottom: index < weeks.length - 1 ? '1px solid var(--cream-2)' : undefined,
              }}
            >
              <div className="mb-3.5 flex items-center gap-2.5">
                <span className="font-[var(--font-display)] text-[15px] font-semibold tracking-[-0.02em] text-[var(--ink-1)]">
                  {week.label}
                </span>
                <span
                  className="rounded-[var(--r-pill)] px-2.5 py-0.5 text-[12px] font-semibold"
                  style={{
                    background: toneTint,
                    color: tone === 'neutral' ? 'var(--ink-3)' : toneAccent,
                  }}
                >
                  {items.length} article{items.length === 1 ? '' : 's'}
                </span>
              </div>
              {items.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {items.map((article) => (
                    <button
                      key={article.id}
                      type="button"
                      onClick={() => onViewArticle?.(article.id)}
                      className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-4 py-3.5 text-left"
                    >
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <ArPill
                          label={article.status === 'ready' ? 'Ready' : 'Draft'}
                          accent={article.status === 'ready' ? 'var(--success)' : 'var(--gold)'}
                          tint={
                            article.status === 'ready' ? 'var(--success-tint)' : 'var(--gold-tint)'
                          }
                        />
                        <span className="rounded-[var(--r-pill)] bg-[var(--cream-1)] px-2 py-0.5 text-[11px] font-medium text-[var(--ink-3)]">
                          {article.category}
                        </span>
                      </div>
                      <div className="text-[14px] font-semibold text-[var(--ink-1)]">{article.title}</div>
                      <div className="mt-1 text-[12px] text-[var(--ink-3)]">
                        {article.wordCount.toLocaleString()} words · Targets &ldquo;{article.targetKeyword}&rdquo;
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-4 text-center text-[13px] text-[var(--ink-4)]">
                  No articles scheduled this week yet.
                </div>
              )}
            </div>
          );
        })
      ) : (
        <div className="rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-4 text-center text-[13px] text-[var(--ink-4)]">
          Calendar will populate as articles are scheduled.
        </div>
      )}
    </ArCard>
  );
}

export interface ArticlesViewProps {
  newArticles?: ArNewArticle[];
  existingArticles?: ArExistingArticle[];
  stats?: ArStat[];
  calendar?: ArCalendarWeek[];
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  loading?: boolean;
  onPageChange?: (page: number) => void;
  onViewArticle?: (id: string) => void;
  onCreateArticle?: (id: string) => void;
  creatingArticleId?: string;
  /** Hub CTA — navigate to the same ArticlePreview create shell. */
  onNewArticle?: () => void;
  initialTab?: ArTab;
}

export function ArticlesView({
  newArticles: newArticlesProp,
  existingArticles: existingArticlesProp,
  stats: statsProp,
  calendar: calendarProp,
  pagination,
  loading = false,
  onPageChange,
  onViewArticle,
  onCreateArticle,
  creatingArticleId,
  onNewArticle,
  initialTab,
}: ArticlesViewProps = {}) {
  const meta = GROWTH_ARTICLES_META;
  const controlled = newArticlesProp !== undefined;
  const newArticles = newArticlesProp ?? AR_NEW_ARTICLES;
  const existingArticles = existingArticlesProp ?? AR_EXISTING_ARTICLES;
  const stats = statsProp ?? AR_STATS;
  const calendar = calendarProp ?? AR_CALENDAR_WEEKS;
  const [tab, setTab] = useState<ArTab>(initialTab ?? 'new');
  const [openNewId, setOpenNewId] = useState('');
  const [openExistId, setOpenExistId] = useState('');

  const tabOptions = useMemo(
    () =>
      AR_TABS.map((option) => ({
        value: option.id,
        label:
          option.id === 'new'
            ? `New Articles (${String(newArticles.length)})`
            : option.id === 'existing'
              ? `Update Existing (${String(existingArticles.length)})`
              : option.label,
      })),
    [existingArticles.length, newArticles.length],
  );

  return (
    <div className="growth-articles-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px] flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 max-w-[720px]">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="doc" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>

        {onNewArticle ? (
          <button
            type="button"
            onClick={onNewArticle}
            className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 px-4 text-sm font-semibold text-[var(--cream-1)] shadow-[var(--sh-1)]"
            style={{ background: 'var(--success)' }}
          >
            <Icon kind="plus" size={16} stroke={2.4} />
            New Article
          </button>
        ) : null}
      </div>

      <div
        className="ar-kpi-grid mb-6 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
      >
        {stats.map((stat) => (
          <ArStatTile key={stat.key} stat={stat} />
        ))}
      </div>

      <div className="mb-5">
        <Segmented
          options={tabOptions}
          value={tab}
          onChange={(value) => {
            setTab(value as ArTab);
          }}
        />
      </div>

      {tab === 'new' ? (
        <div>
          {newArticles.length ? (
            newArticles.map((article) => (
              <ArNewCard
                key={article.id}
                article={article}
                open={openNewId === article.id}
                onToggle={() => {
                  setOpenNewId((current) => (current === article.id ? '' : article.id));
                }}
                {...(onViewArticle ? { onViewArticle } : {})}
                {...(onCreateArticle ? { onCreateArticle } : {})}
                {...(creatingArticleId ? { creatingArticleId } : {})}
              />
            ))
          ) : (
            <ArCard className="px-5 py-6 text-[13px] leading-[1.6] text-[var(--ink-3)]">
              {controlled
                ? 'No new articles yet. ELVA will queue drafts from keyword gaps.'
                : 'No new articles in this view.'}
            </ArCard>
          )}
        </div>
      ) : null}

      {tab === 'existing' ? (
        <div>
          {existingArticles.length ? (
            existingArticles.map((article) => (
              <ArExistingCard
                key={article.id}
                article={article}
                open={openExistId === article.id}
                onToggle={() => {
                  setOpenExistId((current) => (current === article.id ? '' : article.id));
                }}
                {...(onViewArticle ? { onViewArticle } : {})}
              />
            ))
          ) : (
            <ArCard className="px-5 py-6 text-[13px] leading-[1.6] text-[var(--ink-3)]">
              {controlled ? 'No existing articles flagged for update.' : 'No existing articles in this view.'}
            </ArCard>
          )}
        </div>
      ) : null}

      {tab === 'calendar' ? (
        <ArCalendar
          weeks={calendar}
          newArticles={newArticles}
          existingArticles={existingArticles}
          {...(onViewArticle ? { onViewArticle } : {})}
        />
      ) : null}

      {pagination && onPageChange && pagination.totalPages > 1 ? (
        <div className="mt-6 flex justify-center">
          <Pagination
            page={pagination.page}
            total={pagination.totalPages}
            onChange={onPageChange}
          />
        </div>
      ) : null}
    </div>
  );
}
